import { ReactNode, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type Role = "candidate" | "matchmaker";

interface ReportUserDialogProps {
  reportedUserId: string;
  role: Role;
  /** Render-prop trigger. If omitted, the dialog can be controlled externally. */
  trigger?: ReactNode;
  /** Controlled open state (optional). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const REASON_OPTIONS: { value: string; label: string }[] = [
  { value: "inappropriate_behavior", label: "Inappropriate behavior" },
  { value: "off_platform_contact", label: "Off-platform contact attempt" },
  { value: "misleading_information", label: "Misleading information" },
  { value: "harassment", label: "Harassment" },
  { value: "other", label: "Other" },
];

export function ReportUserDialog({
  reportedUserId,
  role,
  trigger,
  open: openProp,
  onOpenChange,
}: ReportUserDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? !!openProp : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const [reason, setReason] = useState<string>("inappropriate_behavior");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setReason("inappropriate_behavior");
    setMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("report-user", {
        body: {
          reported_user_id: reportedUserId,
          role,
          reason,
          message: message.trim(),
        },
      });

      if (error) throw error;
      if (data && data.success === false) {
        throw new Error(data.error || "Failed to submit report");
      }

      toast.success("Thanks. We'll review this.");
      reset();
      setOpen(false);
    } catch (err: unknown) {
      console.error("report-user.submit_failed");
      const errMsg = err instanceof Error ? err.message : null;
      toast.error(
        errMsg
          ? `Could not submit report: ${errMsg}`
          : "Could not submit report. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const subjectLabel =
    role === "matchmaker" ? "Report this matchmaker" : "Report this profile";
  const subjectDescription =
    role === "matchmaker"
      ? "Let us know if a matchmaker has acted inappropriately. Reports go to the MAS Matrimonial team for review."
      : "Let us know if this person has acted inappropriately. Reports go to the MAS Matrimonial team for review.";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{subjectLabel}</DialogTitle>
          <DialogDescription>{subjectDescription}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="report-reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="report-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="report-message">
              Tell us what happened (optional)
            </Label>
            <Textarea
              id="report-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Any context that helps us investigate..."
              rows={5}
              maxLength={5000}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit report"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
